export type AppRole = "admin" | "operator" | "fleet_manager" | "driver";
export type AppWorkLine = "nacional" | "internacional" | "batea";

export interface PermissionContext {
  role: AppRole;
  workLines?: readonly AppWorkLine[] | null;
  isSuperAdmin?: boolean;
}

export type AppResource =
  | "geo"
  | "trips"
  | "shipments"
  | "cargo"
  | "smartgt"
  | "units"
  | "drivers"
  | "clients"
  | "config"
  | "mobiles"
  | "parties"
  | "users"
  | "roles"
  | "audit"
  | "reports"
  | "docs"
  | "contracts"
  | "batea"
  | "shipmentDocTypes";

export type AppAction = "visit" | "write" | "delete" | "restore";

type ResourcePermissions = Record<AppAction, boolean>;
type PermissionMap = Record<AppRole, Record<AppResource, ResourcePermissions>>;

const ALL: ResourcePermissions    = { visit: true,  write: true,  delete: true,  restore: true  };
const READ: ResourcePermissions   = { visit: true,  write: false, delete: false, restore: false };
const NONE: ResourcePermissions   = { visit: false, write: false, delete: false, restore: false };
const VIEW_ONLY: ResourcePermissions = { visit: true, write: false, delete: false, restore: false };

export const PERMISSIONS = {
  admin: {
    geo:       ALL,
    trips:     ALL,
    shipments: ALL,
    cargo:     ALL,
    smartgt:   ALL,
    units:     ALL,
    drivers:   ALL,
    clients:   ALL,
    config:    ALL,
    mobiles:   ALL,
    parties:   ALL,
    users:     ALL,
    roles:     NONE,
    audit:     NONE,
    reports:   NONE,
    docs:      ALL,
    contracts: ALL,
    batea:     ALL,
    shipmentDocTypes: ALL,
  },
  operator: {
    geo:       ALL,
    trips:     ALL,
    shipments: ALL,
    cargo:     ALL,
    smartgt:   ALL,
    units:     ALL,
    drivers:   ALL,
    clients:   ALL,
    config:    ALL,
    mobiles:   ALL,
    parties:   ALL,
    users:     ALL,
    roles:     NONE,
    audit:     NONE,
    reports:   NONE,
    docs:      NONE,
    contracts: ALL,
    batea:     ALL,
    shipmentDocTypes: READ,
  },
  fleet_manager: {
    geo:       NONE,
    trips:     NONE,
    shipments: NONE,
    cargo:     NONE,
    smartgt:   NONE,
    units:     NONE,
    drivers:   NONE,
    clients:   NONE,
    config:    NONE,
    mobiles:   NONE,
    parties:   NONE,
    users:     NONE,
    roles:     NONE,
    audit:     NONE,
    reports:   NONE,
    docs:      NONE,
    contracts: NONE,
    batea:     NONE,
    shipmentDocTypes: NONE,
  },
  driver: {
    geo:       NONE,
    trips:     NONE,
    shipments: NONE,
    cargo:     NONE,
    smartgt:   NONE,
    units:     NONE,
    drivers:   NONE,
    clients:   NONE,
    config:    NONE,
    mobiles:   NONE,
    parties:   NONE,
    users:     NONE,
    roles:     NONE,
    audit:     NONE,
    reports:   NONE,
    docs:      NONE,
    contracts: NONE,
    batea:     NONE,
    shipmentDocTypes: NONE,
  },
} satisfies PermissionMap;

/** Returns true when a user can access a specific work line. */
export function hasWorkLineAccess(user: PermissionContext, workLine: AppWorkLine): boolean {
  if (user.isSuperAdmin || user.role === "admin") return true
  return user.role === "operator" && user.workLines?.includes(workLine) === true
}

/** Returns true if the given role has the given permission. */
export function hasPermission(role: AppRole, resource: AppResource, action: AppAction): boolean {
  return PERMISSIONS[role]?.[resource]?.[action] ?? false;
}

/** Returns the list of roles that have a given permission — useful for route guards. */
export function rolesWithPermission(resource: AppResource, action: AppAction): AppRole[] {
  return (Object.keys(PERMISSIONS) as AppRole[]).filter(r =>
    hasPermission(r, resource, action)
  );
}
